import { salvarRascunho, carregarRascunho } from './db'
import { getAtividades } from './activities'
import { atualizarChecklistIdentificacao } from './progress'
import type { DraftData, IdentificacaoParceiros } from './types'

function typedKeys<T extends object>(obj: T) {
  return Object.keys(obj) as Array<keyof T>
}

export const IDS: Record<keyof IdentificacaoParceiros, string> = {
  // 1º PARCEIRO – ICT Pública
  razaoSocial: 'razaoSocial',
  cnpj: 'cnpj',
  endereco: 'endereco',
  representanteLegal: 'representanteLegal',
  cargo: 'cargo',
  telefone: 'telefone',
  email: 'email',

  // 2º PARCEIRO
  nomeParceiro: 'nomeParceiro',
  razaoSocial2: 'razaoSocial2',
  cnpj2: 'cnpj2',
  endereco2: 'endereco2',
  representanteLegal2: 'representanteLegal2',
  cargo2: 'cargo2',
  telefone2: 'telefone2',
  email2: 'email2',

  // FUNDAÇÃO DE APOIO
  razaoSocialFundacao: 'razaoSocialFundacao',
  cnpjFundacao: 'cnpjFundacao',
  enderecoFundacao: 'enderecoFundacao',
  representanteLegalFundacao: 'representanteLegalFundacao',
  cargoFundacao: 'cargoFundacao',
  telefoneFundacao: 'telefoneFundacao',
  emailFundacao: 'emailFundacao',

  // COORDENAÇÃO – UFC
  coordenador: 'coordenador',
  siape: 'siape',
  lotacao: 'lotacao',
  telefoneUFC: 'telefoneUFC',
  emailUFC: 'emailUFC',

  // COORDENAÇÃO – PARCEIRO
  coordenadorParceiro: 'coordenadorParceiro',
  telefoneParceiro: 'telefoneParceiro',
  emailParceiro: 'emailParceiro'
}

function input(id: string): HTMLInputElement | null {
  return document.getElementById(id) as HTMLInputElement | null
}

export function getIdentificacao(): IdentificacaoParceiros {
  const data: Partial<Record<keyof IdentificacaoParceiros, string>> = {}
  for (const k of typedKeys(IDS)) {
    const el = input(IDS[k])
    if (el) data[k] = el.value.trim()
  }
  return data as IdentificacaoParceiros
}

const ok = (v?: string) => typeof v === 'string' && v.trim().length > 0

function verificarChecklistIdentificacao() {
  const id = getIdentificacao()

  // 1º PARCEIRO – ICT Pública
  const ictOK =
    ok(id.razaoSocial) &&
    ok(id.cnpj) &&
    ok(id.endereco) &&
    ok(id.representanteLegal) &&
    ok(id.cargo) &&
    ok(id.telefone) &&
    ok(id.email)

  // 2º PARCEIRO
  const parceiro2OK =
    ok(id.nomeParceiro) &&
    ok(id.razaoSocial2) &&
    ok(id.cnpj2) &&
    ok(id.endereco2) &&
    ok(id.representanteLegal2) &&
    ok(id.cargo2) &&
    ok(id.telefone2) &&
    ok(id.email2)

  // Coordenação – UFC 
  const coordUfcOK = 
    ok(id.coordenador) && 
    ok(id.siape) &&
    ok(id.lotacao) &&
    ok(id.telefoneUFC) &&
    ok(id.emailUFC)

  // Coordenação – Parceiro 
  const coordParceiroOK = 
    ok(id.coordenadorParceiro) && 
    ok(id.telefoneParceiro) &&
    ok(id.emailParceiro)

  // Fundação de Apoio: opcional (não bloqueia conclusão)
  const completo = ictOK && parceiro2OK && coordUfcOK && coordParceiroOK

  atualizarChecklistIdentificacao(completo)
}

async function persistir() {
  const atividades = getAtividades()
  const id = getIdentificacao()

  const atual = (await carregarRascunho()) || ({} as Partial<DraftData>)

  const payload: DraftData = {
    ...(atual as DraftData),

    ...(id as IdentificacaoParceiros),
    atividades,

    objetivosGerais: atual?.objetivosGerais ?? '',
    objetivosEspecificos: atual?.objetivosEspecificos ?? ''
  }

  await salvarRascunho(payload)
}

function wireInput(id: string) {
  const el = input(id)
  el?.addEventListener('input', () => {
    verificarChecklistIdentificacao()
    void persistir()
  })
}

export function initIdentificacao() {
  for (const id of Object.values(IDS)) wireInput(id)
  verificarChecklistIdentificacao()
}

/** Preenche os inputs com dados salvos (rascunho) */
export function preencherIdentificacao(data: Partial<DraftData>) {
  for (const k of typedKeys(IDS)) {
    const el = input(IDS[k])
    const val = (data as Partial<Record<keyof IdentificacaoParceiros, string>>)[k]
    if (el && typeof val === 'string') el.value = val
  }
  verificarChecklistIdentificacao()
}